import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import { diffOrderedUniqueLists } from './diffOrderedUniqueLists';
import { indexTree, type TreeStrategy } from './indexTree';

export interface DiffTreeInsertion<Item> {
    type: 'added';
    item: Item;
}

export interface DiffTreeDeletion<Item> {
    type: 'deleted';
    item: Item;
}

export interface DiffTreeModification<Item> {
    type: 'modified';
    before: Item;
    item: Item;
    /**
     * Somtimes a modification is accompanied with a move. In scenarios where the item was moved
     * and modified, the `moved` property will be set.
     */
    moved?: {
        beforeIndex: number;
    };
}

export type DiffTreeChange<Item> =
    | DiffTreeInsertion<Item>
    | DiffTreeDeletion<Item>
    | DiffTreeModification<Item>;

export type DiffTreeChangeType<T> = DiffTreeChange<T>['type'];

export interface DiffStrategy<Item> extends TreeStrategy<Item> {
    /**
     * Annotate a node with a diff.
     */
    annotateChange: (item: Item, diff: DiffTreeChange<Item>) => Item[];

    /**
     * Test if a diff should be recorded in the changes map.
     */
    shouldRecordChange?: (
        item: Item,
        diff: DiffTreeChange<Item>,
        annotatedItems: Item[]
    ) => boolean;

    /**
     * Annotate a unchanged node.
     */
    annotateUnchanged?: (item: Item, ctx: { hasDescendantChanges: boolean }) => Item[];
}

export interface DiffResult<Item> {
    items: Item[];
    changes: Map<string, DiffTreeChange<Item>>;
}

/**
 * Compute the differences between two trees and annotate the nodes.
 */
export function diffTrees<Item>(
    trees: {
        before: Item[];
        after: Item[];
    },
    strategy: DiffStrategy<Item>
): DiffResult<Item> {
    const changes = new Map<string, DiffTreeChange<Item>>();

    const beforeTreeIndex = indexTree(trees.before, strategy);

    const diffTreeLevel = (
        before: Item[],
        after: Item[]
    ): {
        items: Item[];
        hasChanges: boolean;
    } => {
        const beforeIndex = new Map<string, Item>();
        const afterIndex = new Map<string, Item>();

        const beforeKeys: string[] = [];
        const afterKeys: string[] = [];

        before.forEach((item) => {
            const key = strategy.getId(item);
            beforeIndex.set(key, item);
            beforeKeys.push(key);
        });
        after.forEach((item) => {
            const key = strategy.getId(item);
            afterIndex.set(key, item);
            afterKeys.push(key);
        });

        const keyDiffs = diffOrderedUniqueLists(beforeKeys, afterKeys);

        let hasChanges = false;
        const result: Item[] = [];

        const pushItemChange = (
            item: Item,
            change: DiffTreeChange<Item> | undefined,
            hasDescendantChanges: boolean
        ): boolean => {
            if (change) {
                const annotated = strategy.annotateChange(item, change);
                const shouldRecordChange =
                    strategy.shouldRecordChange?.(item, change, annotated) ?? true;
                result.push(...annotated);
                if (shouldRecordChange) {
                    changes.set(strategy.getId(item), change);
                }
                return annotated.length > 0;
            }

            if (strategy.annotateUnchanged) {
                result.push(...strategy.annotateUnchanged(item, { hasDescendantChanges }));
            } else {
                result.push(item);
            }
            return hasDescendantChanges;
        };

        keyDiffs.forEach((diff) => {
            switch (diff.op) {
                case '+': {
                    diff.values.forEach((key) => {
                        const afterItem = afterIndex.get(key);
                        assertIsDefined(afterItem);

                        // If the item was added, but already existed somewhere else in the tree
                        // we want to potentially show it as modified instead of added.
                        // Basically it's a move + modification.
                        const beforeTreeItem = beforeTreeIndex.get(key)?.item;
                        const afterDiff = diffTreeLevel([], strategy.getChildren(afterItem));
                        hasChanges =
                            pushItemChange(
                                strategy.setChildren(afterItem, afterDiff.items),
                                beforeTreeItem &&
                                    strategy.compareItems(beforeTreeItem, afterItem) !== 0
                                    ? {
                                          type: 'modified',
                                          before: beforeTreeItem,
                                          item: afterItem,
                                          moved: {
                                              beforeIndex: beforeKeys.indexOf(key),
                                          },
                                      }
                                    : {
                                          type: 'added',
                                          item: afterItem,
                                      },
                                afterDiff.hasChanges
                            ) || hasChanges;
                    });
                    break;
                }
                case '-': {
                    diff.values.forEach((key) => {
                        const beforeItem = beforeIndex.get(key);
                        assertIsDefined(beforeItem);

                        const beforeDiff = diffTreeLevel(strategy.getChildren(beforeItem), []);
                        hasChanges =
                            pushItemChange(
                                strategy.setChildren(beforeItem, beforeDiff.items),
                                {
                                    type: 'deleted',
                                    item: beforeItem,
                                },
                                beforeDiff.hasChanges
                            ) || hasChanges;
                    });
                    break;
                }
                case '=': {
                    diff.values.forEach((key) => {
                        const beforeItem = beforeIndex.get(key);
                        const afterItem = afterIndex.get(key);

                        assertIsDefined(beforeItem);
                        assertIsDefined(afterItem);

                        const isSelfModified = strategy.compareItems(beforeItem, afterItem) !== 0;
                        const selfDiff = diffTreeLevel(
                            strategy.getChildren(beforeItem),
                            strategy.getChildren(afterItem)
                        );
                        hasChanges =
                            pushItemChange(
                                strategy.setChildren(afterItem, selfDiff.items),
                                isSelfModified
                                    ? {
                                          type: 'modified',
                                          before: beforeItem,
                                          item: afterItem,
                                      }
                                    : undefined,
                                selfDiff.hasChanges
                            ) || hasChanges;
                    });
                    break;
                }
                default:
                    assertNever(diff);
            }
        });

        return { items: result, hasChanges };
    };

    const { items: topItems } = diffTreeLevel(trees.before, trees.after);
    return {
        items: topItems,
        changes,
    };
}
